/**
 * AP-MATCH-01C: Finance Invoice Review — Matching Detail + Approve/Reject
 * Route: /ap/invoice-review/:invoiceId (internal Finance — FINANCE_APPROVER, TENANT_ADMIN)
 *
 * Shows: Invoice header, matching rules table, PO comparison, approval history, A/R actions.
 * Gate 01D: Requires ap.invoice.approve / ap.invoice.reject capability.
 * Gate 01E: Approve/Reject transitions are immutable.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, ChevronRight, CheckCircle2, XCircle,
  AlertTriangle, Clock, Play, ThumbsUp, ThumbsDown,
  Shield, FileText, Info,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CFG = {
  SUBMITTED:    { label: "Submitted",    color: "#1D4ED8", bg: "#EFF6FF", border: "#BFDBFE" },
  VALIDATING:   { label: "Validating",   color: "#92400E", bg: "#FEF3C7", border: "#FDE68A" },
  MATCHED:      { label: "Matched",      color: "#166534", bg: "#F0FDF4", border: "#BBF7D0" },
  EXCEPTION:    { label: "Exception",    color: "#9A3412", bg: "#FFF7ED", border: "#FED7AA" },
  APPROVED:     { label: "Approved",     color: "#14532D", bg: "#DCFCE7", border: "#86EFAC" },
  REJECTED:     { label: "Rejected",     color: "#991B1B", bg: "#FEF2F2", border: "#FECACA" },
};

function fmt(minor, currency = "INR") {
  if (minor == null) return "—";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency, minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(minor / 100);
}
function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}
function fmtQty(qty) {
  if (qty == null || qty === "") return "—";
  const n = parseFloat(qty);
  return isNaN(n) ? qty : n % 1 === 0 ? String(Math.round(n)) : n.toFixed(3).replace(/\.?0+$/, "");
}

function RuleBadge({ result }) {
  if (result === "PASS") return <span style={{ color: "#166534", fontSize: "11px", fontWeight: 700, background: "#DCFCE7", padding: "2px 7px", borderRadius: "10px" }}>PASS</span>;
  if (result === "FAIL") return <span style={{ color: "#991B1B", fontSize: "11px", fontWeight: 700, background: "#FEE2E2", padding: "2px 7px", borderRadius: "10px" }}>FAIL</span>;
  return <span style={{ color: "#92400E", fontSize: "11px", fontWeight: 700, background: "#FEF3C7", padding: "2px 7px", borderRadius: "10px" }}>WARN</span>;
}

export default function FinanceInvoiceReview() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionBusy, setActionBusy] = useState(false);

  const [approveNotes, setApproveNotes] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [rejectNotes, setRejectNotes] = useState("");
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [triggering, setTriggering] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/ap/match/${invoiceId}`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load invoice");
    } finally {
      setLoading(false);
    }
  }, [invoiceId]);

  useEffect(() => { load(); }, [load]);

  async function handleTriggerMatch() {
    setTriggering(true);
    try {
      const res = await axios.post(`${API}/api/ap/match/trigger/${invoiceId}`, {}, { withCredentials: true });
      toast.success(`Matching complete: ${res.data.match_status} (${res.data.passed_count} passed, ${res.data.failed_count} failed)`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Matching failed");
    } finally {
      setTriggering(false);
    }
  }

  async function handleApprove() {
    setActionBusy(true);
    try {
      await axios.post(`${API}/api/ap/match/${invoiceId}/approve`, { notes: approveNotes || null }, { withCredentials: true });
      toast.success("Invoice approved — payable recorded");
      setShowApproveModal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Approval failed");
    } finally {
      setActionBusy(false);
    }
  }

  async function handleReject() {
    if (!rejectReason.trim()) { toast.error("Rejection reason is required"); return; }
    setActionBusy(true);
    try {
      await axios.post(`${API}/api/ap/match/${invoiceId}/reject`, { reason: rejectReason, notes: rejectNotes || null }, { withCredentials: true });
      toast.success("Invoice rejected — supplier commitment released");
      setShowRejectModal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Rejection failed");
    } finally {
      setActionBusy(false);
    }
  }

  if (loading) return (
    <div style={{ minHeight: "100vh", background: "#F8F9FA", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <p style={{ color: "#9CA3AF", fontSize: "14px" }}>Loading invoice review…</p>
    </div>
  );

  if (error || !data) return (
    <div style={{ minHeight: "100vh", background: "#F8F9FA", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center" }}>
        <AlertTriangle size={40} style={{ color: "#DC2626", marginBottom: "16px" }} />
        <p style={{ fontSize: "15px", color: "#DC2626" }}>{error || "Not found"}</p>
        <button onClick={() => navigate("/ap/invoice-review")} style={{ marginTop: "16px", padding: "8px 18px", background: "#1A1A1A", color: "#C9A15F", border: "1px solid #C9A15F", borderRadius: "6px", cursor: "pointer", fontSize: "13px" }}>
          Back to Queue
        </button>
      </div>
    </div>
  );

  const { invoice, po_summary, match_result, approval_decisions } = data;
  const status = invoice.status;
  const statusCfg = STATUS_CFG[status] || {};
  const canMatch    = status === "SUBMITTED";
  const canApprove  = ["MATCHED", "EXCEPTION"].includes(status);
  const canReject   = ["SUBMITTED", "VALIDATING", "MATCHED", "EXCEPTION"].includes(status);
  const isTerminal  = ["APPROVED", "REJECTED", "VOIDED"].includes(status);

  return (
    <div data-testid="finance-invoice-review-page" style={{ minHeight: "100vh", background: "#F8F9FA", fontFamily: "'DM Sans', sans-serif" }}>
      {/* Nav */}
      <nav style={{ background: "#1A1A1A", borderBottom: "1px solid rgba(201,161,95,0.2)", padding: "0 24px", height: "60px", display: "flex", alignItems: "center", gap: "16px", position: "sticky", top: 0, zIndex: 10 }}>
        <button onClick={() => navigate("/ap/invoice-review")} data-testid="back-to-queue-btn"
          style={{ display: "flex", alignItems: "center", gap: "6px", background: "none", border: "none", color: "#8A7A6A", cursor: "pointer", fontSize: "13px" }}>
          <ArrowLeft size={16} /> Invoice Queue
        </button>
        <ChevronRight size={14} style={{ color: "#555" }} />
        <span style={{ fontFamily: "monospace", fontSize: "13px", color: "#C9A15F" }}>
          {invoice.ap_reference || "Review"}
        </span>
      </nav>

      <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "32px 24px" }}>
        {/* Header */}
        <div data-testid="review-invoice-header" style={{ background: "#1A1A1A", borderRadius: "12px", padding: "28px", marginBottom: "20px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "16px" }}>
            <div>
              <p style={{ fontSize: "11px", color: "#6B5B4A", margin: "0 0 6px", textTransform: "uppercase", letterSpacing: "1px" }}>AP Reference</p>
              <h2 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "26px", fontWeight: 600, color: "#F5EFE6", margin: "0 0 12px" }}>
                {invoice.ap_reference || invoice.supplier_invoice_number || "Draft"}
              </h2>
              <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", padding: "5px 14px", borderRadius: "20px", fontSize: "13px", fontWeight: 700, background: statusCfg.bg, color: statusCfg.color, border: `1px solid ${statusCfg.border}` }}>
                {status}
              </span>
            </div>
            <div style={{ textAlign: "right" }}>
              <p style={{ fontSize: "11px", color: "#8A7A6A", margin: "0 0 4px", textTransform: "uppercase" }}>Invoice Total</p>
              <p style={{ fontSize: "28px", fontWeight: 700, color: "#C9A15F", margin: 0 }}>
                {fmt(invoice.total_minor, invoice.currency_code)}
              </p>
            </div>
          </div>
          <div style={{ display: "flex", gap: "32px", marginTop: "24px", borderTop: "1px solid rgba(255,255,255,0.07)", paddingTop: "20px", flexWrap: "wrap" }}>
            {[
              { label: "Supplier Invoice", value: invoice.supplier_invoice_number },
              { label: "Invoice Date",     value: fmtDate(invoice.invoice_date) },
              { label: "Purchase Order",   value: invoice.po_number || po_summary?.po_number },
              { label: "Submitted",        value: fmtDateTime(invoice.submitted_at) },
              { label: "PO Status",        value: po_summary?.status || "—" },
            ].map(({ label, value }) => (
              <div key={label}>
                <p style={{ fontSize: "10px", color: "#6B5B4A", margin: "0 0 3px", textTransform: "uppercase" }}>{label}</p>
                <p style={{ fontSize: "13px", color: "#F5EFE6", margin: 0, fontWeight: 500 }}>{value || "—"}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        {!isTerminal && (
          <div style={{ display: "flex", gap: "12px", marginBottom: "20px", flexWrap: "wrap" }}>
            {canMatch && (
              <button data-testid="trigger-match-btn" disabled={triggering} onClick={handleTriggerMatch}
                style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 20px", background: "#1E40AF", color: "#FFF", border: "none", borderRadius: "8px", fontSize: "13px", fontWeight: 700, cursor: "pointer" }}>
                <Play size={15} />
                {triggering ? "Running Match…" : "Run Three-Way Match"}
              </button>
            )}
            {canApprove && (
              <button data-testid="approve-invoice-btn" onClick={() => setShowApproveModal(true)}
                style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 20px", background: "#16A34A", color: "#FFF", border: "none", borderRadius: "8px", fontSize: "13px", fontWeight: 700, cursor: "pointer" }}>
                <ThumbsUp size={15} />
                Approve Invoice
              </button>
            )}
            {canReject && (
              <button data-testid="reject-invoice-btn" onClick={() => setShowRejectModal(true)}
                style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 20px", background: "transparent", border: "1px solid #FCA5A5", color: "#DC2626", borderRadius: "8px", fontSize: "13px", fontWeight: 600, cursor: "pointer" }}>
                <ThumbsDown size={15} />
                Reject Invoice
              </button>
            )}
          </div>
        )}

        {/* Invoice Lines */}
        <div style={{ background: "#FFF", border: "1px solid #E5E7EB", borderRadius: "12px", overflow: "hidden", marginBottom: "20px" }}>
          <div style={{ padding: "14px 20px", borderBottom: "1px solid #E5E7EB" }}>
            <h3 style={{ fontSize: "13px", fontWeight: 700, color: "#111827", margin: 0, textTransform: "uppercase", letterSpacing: "0.4px" }}>
              Invoice Lines
            </h3>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#F9FAFB" }}>
                  {["Description", "Unit", "Qty", "Unit Rate", "Subtotal", "Tax Rate", "Tax", "Total"].map((h, i) => (
                    <th key={i} style={{ padding: "10px 14px", textAlign: i >= 2 ? "right" : "left", fontSize: "11px", fontWeight: 700, color: "#6B7280", borderBottom: "1px solid #E5E7EB", textTransform: "uppercase" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(invoice.lines || []).map((line, idx) => (
                  <tr key={idx} style={{ borderBottom: "1px solid #F3F4F6" }}>
                    <td style={{ padding: "12px 14px", fontSize: "13px", color: "#111827" }}>{line.description || "—"}</td>
                    <td style={{ padding: "12px 14px", fontSize: "12px", color: "#6B7280" }}>{line.unit || "—"}</td>
                    <td style={{ padding: "12px 14px", textAlign: "right", fontWeight: 600 }}>{fmtQty(line.invoiced_quantity)}</td>
                    <td style={{ padding: "12px 14px", textAlign: "right", fontSize: "12px" }}>{fmt(line.unit_rate_minor, invoice.currency_code)}</td>
                    <td style={{ padding: "12px 14px", textAlign: "right", fontSize: "12px" }}>{fmt(line.subtotal_minor, invoice.currency_code)}</td>
                    <td style={{ padding: "12px 14px", textAlign: "right", fontSize: "12px", color: "#6B7280" }}>{((line.tax_rate_bps || 0) / 100).toFixed(1)}%</td>
                    <td style={{ padding: "12px 14px", textAlign: "right", fontSize: "12px" }}>{fmt(line.tax_amount_minor, invoice.currency_code)}</td>
                    <td style={{ padding: "12px 14px", textAlign: "right", fontWeight: 600 }}>{fmt(line.line_total_minor, invoice.currency_code)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: "#F9FAFB", borderTop: "2px solid #E5E7EB" }}>
                  <td colSpan={5} />
                  <td colSpan={2} style={{ padding: "12px 14px", textAlign: "right", fontSize: "12px", fontWeight: 600, color: "#6B7280" }}>
                    Tax: {fmt(invoice.tax_total_minor, invoice.currency_code)}
                  </td>
                  <td style={{ padding: "12px 14px", textAlign: "right", fontSize: "14px", fontWeight: 700, color: "#111827" }}>
                    {fmt(invoice.total_minor, invoice.currency_code)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Matching Result */}
        {match_result ? (
          <div style={{ background: "#FFF", border: "1px solid #E5E7EB", borderRadius: "12px", overflow: "hidden", marginBottom: "20px" }}>
            <div style={{ padding: "14px 20px", borderBottom: "1px solid #E5E7EB", display: "flex", alignItems: "center", gap: "12px" }}>
              <Shield size={16} style={{ color: match_result.match_status === "MATCHED" ? "#16A34A" : "#EA580C" }} />
              <h3 style={{ fontSize: "13px", fontWeight: 700, color: "#111827", margin: 0, textTransform: "uppercase", letterSpacing: "0.4px" }}>
                Three-Way Matching — {match_result.match_status}
              </h3>
              <span style={{ marginLeft: "auto", fontSize: "12px", color: "#6B7280" }}>
                {match_result.passed_rules.length} passed · {match_result.failed_rules.length} failed · matched {fmtDateTime(match_result.matched_at)}
              </span>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#F9FAFB" }}>
                    {["Rule", "Result", "Description", "Expected", "Actual"].map((h, i) => (
                      <th key={i} style={{ padding: "9px 14px", textAlign: "left", fontSize: "11px", fontWeight: 700, color: "#6B7280", borderBottom: "1px solid #E5E7EB", textTransform: "uppercase" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(match_result.rules_evaluated || []).map((rule, idx) => (
                    <tr key={idx} data-testid={`rule-row-${rule.rule_code}`}
                      style={{ borderBottom: "1px solid #F3F4F6", background: rule.rule_result === "FAIL" ? "#FFF5F5" : "transparent" }}>
                      <td style={{ padding: "10px 14px", fontSize: "11px", fontFamily: "monospace", color: "#374151", whiteSpace: "nowrap" }}>
                        {rule.rule_code}
                      </td>
                      <td style={{ padding: "10px 14px" }}>
                        <RuleBadge result={rule.rule_result} />
                      </td>
                      <td style={{ padding: "10px 14px", fontSize: "12px", color: "#374151", maxWidth: "300px" }}>
                        {rule.description}
                      </td>
                      <td style={{ padding: "10px 14px", fontSize: "11px", color: "#6B7280", fontFamily: "monospace" }}>
                        {rule.expected ?? "—"}
                      </td>
                      <td style={{ padding: "10px 14px", fontSize: "11px", color: rule.rule_result === "FAIL" ? "#DC2626" : "#374151", fontFamily: "monospace" }}>
                        {rule.actual ?? "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div style={{ background: "#FFF", border: "1px solid #E5E7EB", borderRadius: "12px", padding: "32px", textAlign: "center", marginBottom: "20px" }}>
            <Info size={32} style={{ color: "#CBD5E1", marginBottom: "12px" }} />
            <p style={{ fontSize: "14px", color: "#6B7280", margin: "0 0 16px" }}>
              Three-way matching has not been run yet
            </p>
            {canMatch && (
              <button data-testid="trigger-match-empty-btn" disabled={triggering} onClick={handleTriggerMatch}
                style={{ display: "inline-flex", alignItems: "center", gap: "8px", padding: "10px 20px", background: "#1E40AF", color: "#FFF", border: "none", borderRadius: "8px", fontSize: "13px", fontWeight: 700, cursor: "pointer" }}>
                <Play size={15} />
                {triggering ? "Running…" : "Run Three-Way Match"}
              </button>
            )}
          </div>
        )}

        {/* Approval History */}
        {approval_decisions && approval_decisions.length > 0 && (
          <div style={{ background: "#FFF", border: "1px solid #E5E7EB", borderRadius: "12px", overflow: "hidden", marginBottom: "20px" }}>
            <div style={{ padding: "14px 20px", borderBottom: "1px solid #E5E7EB" }}>
              <h3 style={{ fontSize: "13px", fontWeight: 700, color: "#111827", margin: 0, textTransform: "uppercase", letterSpacing: "0.4px" }}>Approval History</h3>
            </div>
            <div style={{ padding: "16px 20px" }}>
              {approval_decisions.map((d, i) => (
                <div key={i} data-testid={`approval-decision-${i}`}
                  style={{ display: "flex", alignItems: "flex-start", gap: "14px", padding: "12px 0", borderBottom: i < approval_decisions.length - 1 ? "1px solid #F3F4F6" : "none" }}>
                  {d.decision === "APPROVED"
                    ? <CheckCircle2 size={18} style={{ color: "#16A34A", flexShrink: 0, marginTop: "2px" }} />
                    : <XCircle     size={18} style={{ color: "#DC2626", flexShrink: 0, marginTop: "2px" }} />
                  }
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px" }}>
                      <span style={{ fontSize: "13px", fontWeight: 700, color: d.decision === "APPROVED" ? "#16A34A" : "#DC2626" }}>
                        {d.decision === "APPROVED" ? "Invoice Approved" : "Invoice Rejected"}
                      </span>
                      <span style={{ fontSize: "11px", color: "#9CA3AF" }}>{fmtDateTime(d.decided_at)}</span>
                    </div>
                    <p style={{ fontSize: "12px", color: "#6B7280", margin: "3px 0 0" }}>
                      by {d.decided_by_email}
                      {d.rejection_reason ? ` — Reason: ${d.rejection_reason}` : ""}
                    </p>
                    {d.decision_notes && <p style={{ fontSize: "12px", color: "#374151", margin: "4px 0 0" }}>{d.decision_notes}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Approve Modal */}
      {showApproveModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
          <div data-testid="approve-modal" style={{ background: "#FFF", borderRadius: "12px", padding: "28px", maxWidth: "440px", width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
            <div style={{ display: "flex", gap: "12px", marginBottom: "20px" }}>
              <ThumbsUp size={20} style={{ color: "#16A34A", flexShrink: 0, marginTop: "2px" }} />
              <div>
                <h3 style={{ fontSize: "15px", fontWeight: 700, margin: "0 0 6px" }}>Approve Invoice</h3>
                <p style={{ fontSize: "13px", color: "#6B7280", margin: 0 }}>
                  Approve <strong>{invoice.ap_reference}</strong> for {fmt(invoice.total_minor, invoice.currency_code)}.
                  {status === "EXCEPTION" && " This invoice has matching exceptions — notes are required."}
                </p>
              </div>
            </div>
            <div style={{ marginBottom: "16px" }}>
              <label style={{ fontSize: "12px", fontWeight: 600, color: "#374151", display: "block", marginBottom: "6px" }}>
                Notes {status === "EXCEPTION" ? "*" : "(optional)"}
              </label>
              <textarea data-testid="approve-notes-input" value={approveNotes} onChange={(e) => setApproveNotes(e.target.value)}
                rows={3} placeholder={status === "EXCEPTION" ? "Required: explain exception override…" : "Optional approval notes…"}
                style={{ width: "100%", padding: "10px 12px", border: "1px solid #D1D5DB", borderRadius: "8px", fontSize: "13px", resize: "vertical", boxSizing: "border-box" }} />
            </div>
            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button onClick={() => setShowApproveModal(false)} style={{ padding: "8px 16px", background: "transparent", border: "1px solid #D1D5DB", borderRadius: "6px", cursor: "pointer", fontSize: "13px" }}>Cancel</button>
              <button data-testid="confirm-approve-btn" onClick={handleApprove} disabled={actionBusy || (status === "EXCEPTION" && !approveNotes.trim())}
                style={{ padding: "8px 20px", background: "#16A34A", color: "#FFF", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "13px", fontWeight: 600 }}>
                {actionBusy ? "Approving…" : "Confirm Approve"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
          <div data-testid="reject-modal" style={{ background: "#FFF", borderRadius: "12px", padding: "28px", maxWidth: "440px", width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
            <div style={{ display: "flex", gap: "12px", marginBottom: "20px" }}>
              <ThumbsDown size={20} style={{ color: "#DC2626", flexShrink: 0, marginTop: "2px" }} />
              <div>
                <h3 style={{ fontSize: "15px", fontWeight: 700, margin: "0 0 6px" }}>Reject Invoice</h3>
                <p style={{ fontSize: "13px", color: "#6B7280", margin: 0 }}>Rejecting this invoice will release the supplier's committed quantities, allowing re-invoicing.</p>
              </div>
            </div>
            <div style={{ marginBottom: "12px" }}>
              <label style={{ fontSize: "12px", fontWeight: 600, color: "#374151", display: "block", marginBottom: "6px" }}>Rejection Reason *</label>
              <input data-testid="reject-reason-input" type="text" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                placeholder="e.g. Incorrect unit rate, quantity mismatch…"
                style={{ width: "100%", padding: "10px 12px", border: "1px solid #D1D5DB", borderRadius: "8px", fontSize: "13px", boxSizing: "border-box" }} />
            </div>
            <div style={{ marginBottom: "16px" }}>
              <label style={{ fontSize: "12px", fontWeight: 600, color: "#374151", display: "block", marginBottom: "6px" }}>Additional Notes (optional)</label>
              <textarea data-testid="reject-notes-input" value={rejectNotes} onChange={(e) => setRejectNotes(e.target.value)} rows={2}
                placeholder="Further details for the supplier…"
                style={{ width: "100%", padding: "10px 12px", border: "1px solid #D1D5DB", borderRadius: "8px", fontSize: "13px", resize: "vertical", boxSizing: "border-box" }} />
            </div>
            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button onClick={() => setShowRejectModal(false)} style={{ padding: "8px 16px", background: "transparent", border: "1px solid #D1D5DB", borderRadius: "6px", cursor: "pointer", fontSize: "13px" }}>Cancel</button>
              <button data-testid="confirm-reject-btn" onClick={handleReject} disabled={actionBusy || !rejectReason.trim()}
                style={{ padding: "8px 20px", background: "#DC2626", color: "#FFF", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "13px", fontWeight: 600 }}>
                {actionBusy ? "Rejecting…" : "Confirm Reject"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
